import { useState } from "react";
import { ChevronRight, FileText, Copy, Check } from "lucide-react";

/** Map an arbitrary status string to a tint bucket. */
function tint(status?: string): "passed" | "failed" | "pending" | "" {
  const v = (status ?? "").toLowerCase();
  if (v.includes("pass")) return "passed";
  if (v.includes("fail")) return "failed";
  if (v.includes("pending")) return "pending";
  return "";
}

/**
 * Inline polished code/evidence viewer — a collapsible code-editor style block:
 * header bar (file icon, label, copy), status-tinted border, raw content shown
 * verbatim (wrapped). Used for both the executed Command and the Evidence.
 * Each instance owns its open/copy state.
 */
export function CodeBlock({
  label,
  content,
  status,
}: {
  label: string;
  content: string;
  status?: string;
}) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const st = tint(status);
  const passed = st === "passed";
  const pending = st === "pending";
  const barLabel = label.split(" ")[0].toLowerCase();
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable — ignore */
    }
  };
  const border = passed
    ? "border-emerald-200 dark:border-emerald-500/25"
    : pending
      ? "border-amber-200 dark:border-amber-500/25"
      : "border-red-200 dark:border-red-500/25";
  const headerBg = passed
    ? "bg-emerald-50/70 dark:bg-emerald-500/[0.08]"
    : pending
      ? "bg-amber-50/70 dark:bg-amber-500/[0.08]"
      : "bg-red-50/70 dark:bg-red-500/[0.08]";
  return (
    <div>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 text-[11px] font-bold tracking-wide text-[#475569] dark:text-white/70 hover:text-indigo-600 dark:hover:text-violet-300 transition-colors"
      >
        <ChevronRight className={`size-3.5 transition-transform ${open ? "rotate-90" : ""}`} />
        {label}
      </button>
      {open && (
        <div className={`mt-1.5 rounded-lg border overflow-hidden ${border}`}>
          {/* Code header bar */}
          <div className={`flex items-center gap-2 px-3 py-1.5 border-b ${border} ${headerBg}`}>
            <FileText className="size-3.5 text-[#475569] dark:text-white/70 flex-shrink-0" />
            <span className="text-[11px] font-mono font-bold text-[#334155] dark:text-white/80">{barLabel}</span>
            <span className="flex-1" />
            <button
              onClick={copy}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold text-white bg-indigo-600 dark:bg-violet-600 hover:opacity-90 transition-opacity"
            >
              {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
          {/* Raw body */}
          <div className="max-h-72 overflow-auto bg-[#f8fafc] dark:bg-[#0f111a]">
            <pre className="m-0 px-3 py-2 font-mono text-[11px] leading-relaxed whitespace-pre-wrap break-words text-[#334155] dark:text-white/85">{content}</pre>
          </div>
        </div>
      )}
    </div>
  );
}
